import { BookOpenIcon } from '@heroicons/react/24/outline';

import Footer from '@/renderer/Footer';
import Header from '@/renderer/Header';
import { usePageContext } from '@/renderer/utils/usePageContext';
import { CheckIcon } from '@heroicons/react/24/outline';
import AffiliateForm from './components/AffiliateForm';

const joinReasons = [
  {
    key: 'revenue-share',
    content: (
      <>
        <strong>40% revenue share</strong> for 12 months on every new user you refer.
      </>
    ),
  },
  {
    key: 'duration',
    content: (
      <>
        <strong>Earn on first 12 months</strong> of purchases for each referral.
      </>
    ),
  },
  {
    key: 'value',
    content: (
      <>
        <strong>High user value</strong> &mdash; average RAS customer spends $2,000+ per year.
      </>
    ),
  },
  {
    key: 'respected-brand',
    content: (
      <>
        <strong>Respected brand</strong> &mdash; 25+ years of winning verified by betstamp.
      </>
    ),
  },
  {
    key: 'minimum-payout',
    content: (
      <>
        <strong>$50 minimum payout</strong> &mdash; paid monthly by check or cryptocurrency.
      </>
    ),
  },
];

type Stat = {
  description: string;
  stat: string;
};

const STATS: Stat[] = [
  {
    description: 'All-time win rate across all sports with 7,500+ tracked bets',
    stat: '55%+',
  },
  {
    description: 'All-time NFL sides & totals win rate',
    stat: '58%',
  },
  {
    description: 'Percent of NFL picks that beat the closing line',
    stat: '83%',
  },
  {
    description: 'Percent of college picks that beat the closing line',
    stat: '90%',
  },
  {
    description: 'Results verified by betstamp since 2020',
    stat: '100%',
  },
  {
    description: 'Zero hype. No fluff. Just professional, winning bets.',
    stat: 'Zero',
  },
];

export default function Affiliates() {
  const {
    config: { title },
  } = usePageContext();

  return (
    <div className="flex h-40 min-h-[stretch] flex-1 grow flex-col justify-between overflow-hidden">
      <Header title="RAS Affiliate Program" Icon={BookOpenIcon} className="py-2 sm:hidden" />
      <div className="overflow-auto">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="relative mx-auto hidden max-w-2xl pt-20 pb-24 text-center sm:block">
            <h1 className="font-bold text-3xl text-zinc-900 tracking-tight sm:text-4xl dark:text-zinc-200">
              {title}
            </h1>
            <p className="mt-4 text-base text-zinc-600 leading-7 dark:text-zinc-400">
              Refer new users to the most trusted name in sports betting picks and earn 40% of
              everything they spend&mdash;for 12 full months.
            </p>
          </div>
        </div>
        <div className="relative mt-8 px-4 sm:mt-0 sm:px-6 lg:px-8">
          <div className="prose prose-sm prose-zinc dark:prose-invert mx-auto max-w-prose">
            <h2>Why join?</h2>
            <ul role="list" className="mt-4 flex flex-col gap-y-2 pl-0">
              {joinReasons.map(({ key, content }) => (
                <li key={key} className="flex items-center gap-x-2 pl-0">
                  <CheckIcon
                    className="size-6 flex-shrink-0 self-start text-emerald-500"
                    aria-hidden="true"
                  />
                  <span>{content}</span>
                </li>
              ))}
            </ul>

            <hr className="my-20 border-zinc-900/20 dark:border-white/20" />

            <p>
              This isn’t your typical affiliate program. It’s built for serious partners who value
              trust, transparency, and edge.
            </p>
            <h2>Who it's for</h2>
            <p>The RAS affiliate program is perfect for:</p>
            <ul>
              <li>Substack/Beehiv writers</li>
              <li>Podcast & YouTube hosts</li>
              <li>Sharp X (Twitter) accounts</li>
              <li>Discord/Telegram community leaders</li>
              <li>Anyone with targeted content, reach or influence</li>
            </ul>

            <hr className="my-20 border-zinc-900/20 dark:border-white/20" />

            <h2>Why RAS converts</h2>
            <dl className="mx-auto grid grid-cols-1 divide-y divide-zinc-900/10 px-4 sm:grid-cols-2 sm:gap-8 sm:divide-x-0 sm:divide-y-0 sm:px-0 lg:grid-cols-3 lg:grid-rows-2 dark:divide-white/10">
              {STATS.map(({ stat, description }, index) => (
                <div key={index} className="w-full py-6 text-left sm:py-0 lg:w-fit">
                  <div className="flex flex-col-reverse space-y-2">
                    <dt className="font-normal text-sm text-zinc-500 leading-5">{description}</dt>
                    <dd className="pl-0 font-semibold text-4xl tracking-tight">{stat}</dd>
                  </div>
                </div>
              ))}
            </dl>

            <hr className="my-20 border-zinc-900/20 dark:border-white/20" />

            <p>
              This isn’t your typical affiliate program. It’s built for serious partners who value
              trust, transparency, and edge.
            </p>
            <h2>How It Works</h2>
            <ol>
              <li>Apply and get your custom link</li>
              <li>Share it wherever you have reach</li>
              <li>Earn 40% on every purchase by your referrals</li>
              <li>Get paid monthly</li>
            </ol>
            <p>Earn on first 12 months of orders for every new referral.</p>

            <hr className="my-20 border-zinc-900/20 dark:border-white/20" />

            <h2>Your Earnings</h2>
            <p>
              RAS main products range in price from NFL at $995 to CFB at $2495, to CBB at over
              $3,000 per season. One sign-up to the NFL service would net you close to $400 in
              revenue, which beats any CPA currently offered by sportsbook or sweepstakes sites, and
              you'd still have upside earn from there.
            </p>

            <hr className="my-20 border-zinc-900/20 dark:border-white/20" />

            <h2>Apply Now</h2>
            <p>No gimmicks. No inflated records. Just real pros and real upside.</p>
            <p>
              If you believe in what we do&mdash;and want to share in the success&mdash;apply today.
            </p>
            <AffiliateForm />
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}
